import { useHubSpotSearchInfinite } from "@/api/integrations/hubspot";
import { cn } from "@/utils";
import {
  HubspotCompanyEntity,
  HubspotContactEntity,
  HubspotDealEntity,
} from "@cohesive/api-entities";
import { EntityCardSkeleton } from "@cohesive/ui-components";
import { debounce } from "lodash";
import { Building2, Handshake, User } from "lucide-react";
import React, { useCallback, useMemo, useRef, useState } from "react";
import { EntityDragRow } from "../../dnd/EntityDragRow";
import { BaseRecord, BaseSelectorProps } from "../BaseSelector";
import {
  Selector,
  SelectorEmpty,
  SelectorResultItem,
  SelectorResults,
  SelectorSearch,
  SelectorSidebar,
} from "../Selector";

type ObjectType = "contacts" | "companies" | "deals";

type HubSpotEntity =
  | HubspotContactEntity
  | HubspotCompanyEntity
  | HubspotDealEntity;

interface ObjectTypeConfig {
  label: string;
  icon: React.ReactNode;
  formatResult: (entity: HubSpotEntity) => BaseRecord;
}

export const HubSpotSelector: React.FC<BaseSelectorProps> = ({
  connection,
  onSelect,
}) => {
  const [selectedType, setSelectedType] = useState<ObjectType>("contacts");
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const scrollRef = useRef<HTMLDivElement>(null);

  // Configure how each object type should be displayed and formatted
  const objectTypes: Record<ObjectType, ObjectTypeConfig> = {
    contacts: {
      label: "Contacts",
      icon: <User className="h-4 w-4" />,
      formatResult: (entity) => {
        const data = entity.data as HubspotContactEntity["data"];
        return {
          id: data.id,
          title: data.name || "Unnamed Contact",
          subtitle: data.email || "",
        };
      },
    },
    companies: {
      label: "Companies",
      icon: <Building2 className="h-4 w-4" />,
      formatResult: (entity) => {
        const data = entity.data as HubspotCompanyEntity["data"];
        return {
          id: data.id,
          title: data.name || "Unnamed Company",
          subtitle: data.website || "",
        };
      },
    },
    deals: {
      label: "Deals",
      icon: <Handshake className="h-4 w-4" />,
      formatResult: (entity) => {
        const data = entity.data as HubspotDealEntity["data"];
        return {
          id: data.id,
          title: data.name || "Unnamed Deal",
          subtitle: data.amount ? `${data.amount}` : "0",
        };
      },
    },
  };

  // Debounced search handler
  const debouncedSetSearch = useMemo(
    () =>
      debounce((query: string) => {
        setDebouncedQuery(query);
      }, 500),
    [],
  );

  // API query with infinite scrolling
  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useHubSpotSearchInfinite({
      integrationConnectionId: connection.id,
      provider: connection.integration_provider,
      objectType: selectedType,
      query: debouncedQuery,
    });

  // Flatten all results from all pages
  const allResults = data?.pages.flatMap((page) => page) || [];

  const hubspotIds = useMemo(
    () => allResults.map((e) => objectTypes[selectedType].formatResult(e).id),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [allResults, selectedType],
  );

  // Handle scroll to load more
  const handleScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
      if (
        scrollHeight - scrollTop <= clientHeight * 1.2 &&
        hasNextPage &&
        !isFetchingNextPage
      ) {
        fetchNextPage();
      }
    },
    [fetchNextPage, hasNextPage, isFetchingNextPage],
  );

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    debouncedSetSearch(value);
  };

  const handleRecordSelect = (entity: HubSpotEntity) => {
    // The entity is already in the correct format from the toolbox
    onSelect(entity);
  };

  return (
    <Selector>
      <SelectorSidebar>
        {Object.entries(objectTypes).map(([type, config]) => (
          <button
            key={type}
            onClick={() => setSelectedType(type as ObjectType)}
            className={cn(
              "w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-accent/50",
              {
                "bg-accent": selectedType === type,
              },
            )}
          >
            {config.icon}
            <span>{config.label}</span>
          </button>
        ))}
      </SelectorSidebar>
      <SelectorSearch
        value={searchQuery}
        onChange={handleSearchChange}
        placeholder={`Search ${selectedType}...`}
      />
      <SelectorResults
        ref={scrollRef}
        onScroll={handleScroll}
        style={{ paddingBottom: "200px" }}
      >
        {isLoading ? (
          <div className="space-y-2">
            {[1, 2, 3].map((i) => (
              <EntityCardSkeleton
                key={i}
                className="shadow-none last:border-none rounded-none border-t-0 border-r-0 border-l-0"
              />
            ))}
          </div>
        ) : allResults.length === 0 ? (
          <SelectorEmpty message={`No ${selectedType} found`} />
        ) : (
          <div className="divide-y">
            {allResults.map((entity) => {
              const formatted = objectTypes[selectedType].formatResult(entity);
              return (
                <EntityDragRow
                  key={formatted.id}
                  entity={entity}
                  entityId={formatted.id}
                  orderedIds={hubspotIds}
                  onSelect={(ent) => handleRecordSelect(ent as HubSpotEntity)}
                  dragScope="hubspot"
                >
                  <SelectorResultItem
                    icon={objectTypes[selectedType].icon}
                    title={formatted.title}
                    subtitle={formatted.subtitle}
                  />
                </EntityDragRow>
              );
            })}
            {/* Loading indicator for next page */}
            {isFetchingNextPage && (
              <div className="space-y-2">
                {[1, 2, 3].map((i) => (
                  <EntityCardSkeleton
                    key={`loading-${i}`}
                    className="shadow-none last:border-none rounded-none border-t-0 border-r-0 border-l-0"
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </SelectorResults>
    </Selector>
  );
};
